/**
 * The banner that makes a "view as user" session impossible to forget.
 *
 * Mounted once, globally, above every layout — a Super Admin viewing as a head
 * teacher may well navigate onto a public page, and the banner must follow them
 * there. It is not decoration: it is the only thing distinguishing this tab
 * from an ordinary session, because everything else about the tab genuinely IS
 * the target.
 *
 * It also reports refused writes. This app has no toast system, and a blocked
 * request that produced only a console warning would be invisible to the person
 * who most needs to see it — what the page tried to do is very often the fault
 * being investigated.
 */

import { useEffect, useState } from "react";
import { Eye, LogOut, PenLine, ShieldOff } from "lucide-react";

import { useAuth } from "@/components/auth/useAuth";
import {
  VIEW_AS_BLOCKED_EVENT,
  type ViewAsBlockedDetail,
} from "@/lib/supabase/viewAs";

/** How long a refused-write notice stays on screen. */
const BLOCKED_NOTICE_MS = 8000;

function formatCountdown(msRemaining: number): string {
  const total = Math.max(0, Math.floor(msRemaining / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

export function ViewAsBanner() {
  const { viewAs } = useAuth();

  const [remaining, setRemaining] = useState(0);
  const [blocked, setBlocked] = useState<ViewAsBlockedDetail | null>(null);
  const [busy, setBusy] = useState<"exit" | "enable" | null>(null);

  const expiresAt = viewAs?.record.expiresAt;

  // Countdown. The Super Admin needs to know how long they have, because the
  // session ends underneath them whether or not they are mid-task.
  useEffect(() => {
    if (!expiresAt) return;

    const tick = () => setRemaining(Date.parse(expiresAt) - Date.now());
    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [expiresAt]);

  useEffect(() => {
    if (!viewAs) return;

    const onBlocked = (event: Event) => {
      const detail = (event as CustomEvent<ViewAsBlockedDetail>).detail;
      if (detail) setBlocked(detail);
    };

    window.addEventListener(VIEW_AS_BLOCKED_EVENT, onBlocked);
    return () => window.removeEventListener(VIEW_AS_BLOCKED_EVENT, onBlocked);
  }, [viewAs]);

  useEffect(() => {
    if (!blocked) return;
    const timer = window.setTimeout(() => setBlocked(null), BLOCKED_NOTICE_MS);
    return () => window.clearTimeout(timer);
  }, [blocked]);

  // Mark the tab strip too. With several windows open, the banner is only
  // visible once a tab is focused; the title is what stops the Super Admin
  // typing into the wrong one. Pages set their own titles as you navigate, so
  // the prefix is re-applied whenever <title> changes.
  const targetLabel = viewAs?.record.targetName ||
    viewAs?.record.targetEmail || null;
  useEffect(() => {
    if (!targetLabel) return;

    const prefix = `[Viewing as ${targetLabel}] `;
    const apply = () => {
      if (!document.title.startsWith(prefix)) {
        document.title = prefix + document.title.replace(/^\[Viewing as .*?\] /, "");
      }
    };
    apply();

    const titleEl = document.querySelector("title");
    if (!titleEl) return;

    const observer = new MutationObserver(apply);
    observer.observe(titleEl, { childList: true });
    return () => observer.disconnect();
  }, [targetLabel]);

  if (!viewAs) return null;

  const { record, exit, enableActions } = viewAs;
  const name = record.targetName || record.targetEmail || "this user";

  const handleExit = async () => {
    setBusy("exit");
    try {
      await exit();
    } finally {
      setBusy(null);
    }
  };

  const handleEnable = async () => {
    setBusy("enable");
    try {
      await enableActions();
    } catch {
      // The server refused. Staying read-only is the safe outcome, and the
      // console carries the detail.
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      // A colour used nowhere else in the app, so the tab is identifiable from
      // the corner of an eye. z-50 clears AdminHeader's sticky z-30.
      className="sticky top-0 z-50 w-full border-b border-amber-700/40 bg-amber-500 text-amber-950 print:hidden"
      role="status"
      aria-live="polite"
      data-testid="view-as-banner"
    >
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2 text-sm">
        <span className="flex items-center gap-2 font-semibold">
          <Eye className="h-4 w-4 shrink-0" />
          Viewing as {name}
        </span>

        {record.targetRoleName && (
          <span className="opacity-80">{record.targetRoleName}</span>
        )}

        <span className="opacity-80">
          ends in {formatCountdown(remaining)}
        </span>

        <span className="flex items-center gap-1.5 opacity-80">
          {record.actionsEnabled ? (
            <>
              <PenLine className="h-3.5 w-3.5" />
              writes enabled
            </>
          ) : (
            <>
              <ShieldOff className="h-3.5 w-3.5" />
              read-only
            </>
          )}
        </span>

        <span className="ml-auto flex items-center gap-2">
          {!record.actionsEnabled && (
            <button
              type="button"
              onClick={() => void handleEnable()}
              disabled={busy !== null}
              className="rounded-md border border-amber-900/40 px-2.5 py-1 text-xs font-medium hover:bg-amber-400 disabled:opacity-60"
            >
              {busy === "enable" ? "Enabling…" : "Enable actions"}
            </button>
          )}
          <button
            type="button"
            onClick={() => void handleExit()}
            disabled={busy !== null}
            className="flex items-center gap-1.5 rounded-md bg-amber-900 px-2.5 py-1 text-xs font-semibold text-amber-50 hover:bg-amber-950 disabled:opacity-60"
          >
            <LogOut className="h-3.5 w-3.5" />
            {busy === "exit" ? "Exiting…" : "Exit view-as"}
          </button>
        </span>
      </div>

      {blocked && (
        <div className="border-t border-amber-700/40 bg-amber-600/90 px-4 py-1.5 text-xs text-amber-50">
          Blocked <code className="font-mono">{blocked.label}</code> — this
          would have written data as {name}.{" "}
          {!record.actionsEnabled && "Use “Enable actions” if it is needed."}
        </div>
      )}
    </div>
  );
}

export default ViewAsBanner;
